import { ThunkAction, ThunkDispatch } from 'redux-thunk';

import * as Model from '../models';

import { ProjectUpdateReduxAction, UPDATE_PROJECT } from './project-actions';

export const UPDATE_APP_STATE = 'UPDATE_APP_STATE';
export const UPDATE_WORLD_DATA = 'UPDATE_WORLD_DATA';

export type AppStateUpdateFnRequest = Model.AppState;

export type AppStateUpdateActionPayload = Model.AppState;

export type AppStateUpdateReduxAction = Model.ReduxAction<AppStateUpdateActionPayload>;

export type AppStateUpdateFn = ((request: AppStateUpdateFnRequest) => ThunkAction<ng.IPromise<Model.WorldItem>, Model.RootState, object, AppStateUpdateReduxAction>);

export type AppStateUpdateDispatch = ThunkDispatch<Model.RootState, object, AppStateUpdateReduxAction>;

export type AppStateUpdateAction = {
  updateAppState: AppStateUpdateFn;
};

export type AppStateUpdate = (request: AppStateUpdateFnRequest) => ng.IPromise<Model.WorldItem | null>;

export type AppStateUpdateSupport = {
  updateAppState: AppStateUpdate | null;
};

export type WorldDataUpdateFnRequest = Model.WorldData;

export type WorldDataUpdateActionPayload = Model.WorldData;

export type WorldDataUpdateReduxAction = Model.ReduxAction<WorldDataUpdateActionPayload>;

export type WorldDataUpdateFn = ((request: WorldDataUpdateFnRequest) => ThunkAction<ng.IPromise<Model.WorldItem>, Model.RootState, object, WorldDataUpdateReduxAction | ProjectUpdateReduxAction>);

export type WorldDataUpdateDispatch = ThunkDispatch<Model.RootState, object, WorldDataUpdateReduxAction | ProjectUpdateReduxAction>;

export type WorldDataUpdateAction = {
  updateWorldData: WorldDataUpdateFn;
};

export type WorldDataUpdate = (request: WorldDataUpdateFnRequest) => ng.IPromise<Model.WorldItem | null>;

export type WorldDataUpdateSupport = {
  updateWorldData: WorldDataUpdate | null;
};

export type WorldActions = AppStateUpdateAction & WorldDataUpdateAction;

export type WorldActionsService = ($q: ng.IQService) => WorldActions;
export const WORLD_ACTIONS_SERVICE_NAME = 'WorldActionsService';

function updateTextDefinitions<U extends Model.ClientParamDef<any>>(target: U, vocabulary: Model.Vocabulary): U {
  const { nameLabel: nameI18n, descriptionLabel: descI18n } = target;
  if (!nameI18n && !descI18n) {
    return target;
  }
  let { descriptions, name } = target;
  if (nameI18n) {
    name = vocabulary[nameI18n] || name;
  }
  if (descI18n) {
    const desc: string = vocabulary[descI18n];
    descriptions = desc ? [desc] : descriptions;
  }
  return { ...(target as object), ...{ name, descriptions }} as U;
}

function updateSingleSelectionOptionTextDefinitions(paramDef: Model.SelectionClientParamDef, vocabulary: Model.Vocabulary): Model.SelectionClientParamDef {
  const { value, selected } = paramDef;
  return {
    ...updateTextDefinitions(paramDef, vocabulary),
    selected: updateTextDefinitions(selected, vocabulary),
    value: value.map((option: Model.SkuClientParamDef<string>) => {
      return { ...updateTextDefinitions(option, vocabulary) };
    }),
  };
}

function updateMultiSelectionOptionTextDefinitions<T extends Model.ClientParamDef<string>>(
  paramDef: Model.SelectionTypeClientParamDef<T[], T>,
  vocabulary: Model.Vocabulary): Model.SelectionTypeClientParamDef<T[], T> {
  const { value, selected } = paramDef;
  return {
    ...updateTextDefinitions(paramDef, vocabulary),
    selected: selected.map((option: T) => {
      return { ...(updateTextDefinitions(option, vocabulary) as object) } as T;
    }),
    value: value.map((option: T) => {
      return { ...(updateTextDefinitions(option, vocabulary) as object) } as T;
    }),
  };
}

function translateOligoText(oligo: Model.OligoItem, vocabulary: Model.Vocabulary): Model.OligoItem {
  const { definition: currentDefinition } = oligo;
  const { basic: currentBasic, advanced: currentAdvanced } = currentDefinition;
  const { oligoName, oligoValue, researcherName, scale, purification, format, prime5Mod, prime3Mod, delivery } = currentBasic;
  const { alternate, handling } = currentAdvanced;
  const basic = {
    ...updateTextDefinitions(currentBasic, vocabulary),
    oligoName: updateTextDefinitions(oligoName, vocabulary),
    oligoValue: updateTextDefinitions(oligoValue, vocabulary),
    researcherName: updateTextDefinitions(researcherName, vocabulary),
    scale: updateSingleSelectionOptionTextDefinitions(scale, vocabulary),
    purification: updateSingleSelectionOptionTextDefinitions(purification, vocabulary),
    format: updateSingleSelectionOptionTextDefinitions(format, vocabulary),
    prime5Mod: updateSingleSelectionOptionTextDefinitions(prime5Mod, vocabulary),
    prime3Mod: updateSingleSelectionOptionTextDefinitions(prime3Mod, vocabulary),
    delivery: updateSingleSelectionOptionTextDefinitions(delivery, vocabulary),
  };
  const advanced = {
    ...updateTextDefinitions(currentAdvanced, vocabulary),
    alternate: updateMultiSelectionOptionTextDefinitions<Model.SkuTextInputClientParamDef>(alternate, vocabulary),
    handling: updateMultiSelectionOptionTextDefinitions<Model.SkuClientParamDef<string>>(handling, vocabulary),
  };
  const definition: Model.OligoParamDefs = {
    ...currentDefinition,
    basic,
    advanced,
  };
  return { ...oligo, definition };
}

function translateProjectText(project: Model.ProjectItem, vocabulary: Model.Vocabulary): Model.ProjectItem {
  const { oligos: currentOligos } = project;
  const oligos: Model.OligoItem[] = currentOligos.map((oligo: Model.OligoItem) =>
    translateOligoText(oligo, vocabulary));
  return { ...project, ...{ oligos } };
}

export const WorldActionsService: WorldActionsService = ($q: ng.IQService): WorldActions => {
  return {
    updateAppState: (request: Model.AppState) => {
      return (dispatch: AppStateUpdateDispatch,
              getState: () => Model.RootState,
              extraArgs: object) => {
        const deferred: ng.IDeferred<Model.WorldItem> = $q.defer();
        const { world } = getState();
        dispatch({ type: UPDATE_APP_STATE, payload: request });
        deferred.resolve({ ...world, ...{ appState: request } });
        return deferred.promise;
      };
    },
    updateWorldData: (request: Model.WorldData) => {
      return (dispatch: WorldDataUpdateDispatch,
              getState: () => Model.RootState,
              extraArgs: object) => {
        const deferred: ng.IDeferred<Model.WorldItem> = $q.defer();
        const { projects, world } = getState();
        const { vocabulary } = request;
        dispatch({ type: UPDATE_WORLD_DATA, payload: request });
        projects.map((project: Model.ProjectItem) => translateProjectText(project, vocabulary))
                .forEach((project: Model.ProjectItem) => {
                  dispatch({ type: UPDATE_PROJECT, payload: project });
                });
        deferred.resolve({ ...world, ...request });
        return deferred.promise;
      };
    },
  };
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0-0!./src/main/script/actions/world-actions.ts